import type { BenefitCategory, BenefitSummary } from "@yutai/shared"
import { marketCapTierFromValue } from "../../lib/marketCapTier.js"
import { relevanceLevelFromScore } from "../../lib/relevance.js"
import {
  CAP_TIER_OPTIONS,
  FILTER_CATEGORIES,
  FILTER_MONTHS,
  MIN_BENEFIT_VALUE_OPTIONS,
  MIN_YIELD_OPTIONS,
  RELEVANCE_LEVEL_OPTIONS,
} from "./filterConstants.js"
import type { Filters, SortKey, SortOrder } from "./useBenefits.js"

export type FilterDimension =
  | "categories"
  | "months"
  | "deliveryMonths"
  | "industries"
  | "minBenefitValue"
  | "longTermBonus"
  | "relevance"
  | "minDividendYield"
  | "minBenefitYield"
  | "minCombinedYield"
  | "capTiers"

export interface FilterCounts {
  categories: Record<BenefitCategory, number>
  recordMonths: Record<number, number>
  deliveryMonths: Record<number, number>
  industries: Record<string, number>
  minBenefitValue: Record<string, number>
  longTermBonus: Record<string, number>
  relevance: Record<string, number>
  minDividendYield: Record<string, number>
  minBenefitYield: Record<string, number>
  minCombinedYield: Record<string, number>
  capTier: Record<string, number>
}

export function normalizeForSearch(str: string): string {
  return str
    .toLowerCase()
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
}

// 配当利回りと優待利回りは、最低必要株数を保有した場合の投資額を分母とする
// 同一基準の%なので単純合算できる（保有株数が最低必要株数を超える場合は近似が崩れる）。
export function combinedYield(item: BenefitSummary): number | null {
  if (item.dividendYield == null || item.benefitYield == null) return null
  return item.dividendYield + item.benefitYield
}

export function matchesFilters(
  item: BenefitSummary,
  filters: Pick<Filters, FilterDimension | "query">,
  exclude: FilterDimension[] = []
): boolean {
  const skip = new Set(exclude)

  if (filters.query) {
    const q = normalizeForSearch(filters.query)
    const matchesTier = item.tiers.some((tier) => normalizeForSearch(tier.description).includes(q))
    if (
      !normalizeForSearch(item.name).includes(q) &&
      !item.id.includes(q) &&
      !item.nameKana.includes(q) &&
      !normalizeForSearch(item.businessDescription).includes(q) &&
      !matchesTier
    ) {
      return false
    }
  }

  if (!skip.has("categories") && filters.categories.length) {
    if (!filters.categories.some((c) => item.categories.includes(c))) return false
  }

  if (!skip.has("months") && filters.months.length) {
    if (!filters.months.some((m) => item.recordMonths.includes(m))) return false
  }

  if (!skip.has("deliveryMonths") && filters.deliveryMonths.length) {
    if (!filters.deliveryMonths.some((m) => item.deliveryMonths.includes(m))) return false
  }

  if (!skip.has("industries") && filters.industries.length) {
    if (!filters.industries.includes(item.industry)) return false
  }

  if (!skip.has("minBenefitValue") && filters.minBenefitValue !== null) {
    if (item.minimumBenefitValue === null || item.minimumBenefitValue < filters.minBenefitValue)
      return false
  }

  if (!skip.has("longTermBonus") && filters.longTermBonus != null) {
    const years = item.holdingYears ?? []
    if (filters.longTermBonus >= 6) {
      if (!years.some((y) => y >= 6)) return false
    } else {
      if (!years.includes(filters.longTermBonus)) return false
    }
  }

  if (!skip.has("relevance") && filters.relevance.length) {
    const level = relevanceLevelFromScore(item.benefitRelevanceScore)
    if (!level || !filters.relevance.includes(level)) return false
  }

  if (!skip.has("minDividendYield") && filters.minDividendYield !== null) {
    if (item.dividendYield === null || item.dividendYield < filters.minDividendYield) return false
  }

  if (!skip.has("minBenefitYield") && filters.minBenefitYield !== null) {
    if (item.benefitYield === null || item.benefitYield < filters.minBenefitYield) return false
  }

  if (!skip.has("minCombinedYield") && filters.minCombinedYield !== null) {
    const cy = combinedYield(item)
    if (cy === null || cy < filters.minCombinedYield) return false
  }

  if (!skip.has("capTiers") && filters.capTiers.length) {
    const tier = marketCapTierFromValue(item.marketCap)
    if (!tier || !filters.capTiers.includes(tier)) return false
  }

  return true
}

export function filterBenefits(
  data: BenefitSummary[],
  filters: Filters,
  options?: { exclude?: FilterDimension[] }
): BenefitSummary[] {
  return data.filter((item) => matchesFilters(item, filters, options?.exclude))
}

export function sortBenefits(
  data: BenefitSummary[],
  sort: SortKey,
  order: SortOrder
): BenefitSummary[] {
  return [...data].sort((a, b) => {
    switch (sort) {
      case "benefitValue": {
        const aVal = a.minimumBenefitValue
        const bVal = b.minimumBenefitValue
        if (aVal === null && bVal === null) return 0
        if (aVal === null) return 1
        if (bVal === null) return -1
        return order === "asc" ? aVal - bVal : bVal - aVal
      }
      case "combinedYield": {
        const aVal = combinedYield(a)
        const bVal = combinedYield(b)
        if (aVal === null && bVal === null) return 0
        if (aVal === null) return 1
        if (bVal === null) return -1
        return order === "asc" ? aVal - bVal : bVal - aVal
      }
      default:
        return order === "asc" ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id)
    }
  })
}

export function computeFilterCounts(data: BenefitSummary[], filters: Filters): FilterCounts {
  const categories = {} as Record<BenefitCategory, number>
  for (const { value } of FILTER_CATEGORIES) {
    categories[value] = data.filter(
      (item) => item.categories.includes(value) && matchesFilters(item, filters, ["categories"])
    ).length
  }

  const recordMonths = {} as Record<number, number>
  for (const m of FILTER_MONTHS) {
    recordMonths[m] = data.filter(
      (item) => item.recordMonths.includes(m) && matchesFilters(item, filters, ["months"])
    ).length
  }

  const deliveryMonths = {} as Record<number, number>
  for (const m of FILTER_MONTHS) {
    deliveryMonths[m] = data.filter(
      (item) => item.deliveryMonths.includes(m) && matchesFilters(item, filters, ["deliveryMonths"])
    ).length
  }

  const industryValues = Array.from(new Set(data.map((item) => item.industry))).sort((a, b) =>
    a.localeCompare(b, "ja")
  )
  const industries: Record<string, number> = {}
  for (const value of industryValues) {
    industries[value] = data.filter(
      (item) => item.industry === value && matchesFilters(item, filters, ["industries"])
    ).length
  }

  const minBenefitValue: Record<string, number> = {}
  for (const { value } of MIN_BENEFIT_VALUE_OPTIONS) {
    const key = String(value)
    minBenefitValue[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["minBenefitValue"])) return false
      if (value === null) return true
      return item.minimumBenefitValue !== null && item.minimumBenefitValue >= value
    }).length
  }

  const longTermBonus: Record<string, number> = {}
  for (const yr of [1, 2, 3, 4, 5, 6]) {
    longTermBonus[String(yr)] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["longTermBonus"])) return false
      const years = item.holdingYears ?? []
      return yr >= 6 ? years.some((y) => y >= 6) : years.includes(yr)
    }).length
  }

  const relevance: Record<string, number> = {}
  for (const { value } of RELEVANCE_LEVEL_OPTIONS) {
    relevance[value] = data.filter(
      (item) =>
        relevanceLevelFromScore(item.benefitRelevanceScore) === value &&
        matchesFilters(item, filters, ["relevance"])
    ).length
  }

  const minDividendYield: Record<string, number> = {}
  for (const { value } of MIN_YIELD_OPTIONS) {
    const key = String(value)
    minDividendYield[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["minDividendYield"])) return false
      if (value === null) return true
      return item.dividendYield !== null && item.dividendYield >= value
    }).length
  }

  const minBenefitYield: Record<string, number> = {}
  for (const { value } of MIN_YIELD_OPTIONS) {
    const key = String(value)
    minBenefitYield[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["minBenefitYield"])) return false
      if (value === null) return true
      return item.benefitYield !== null && item.benefitYield >= value
    }).length
  }

  const minCombinedYield: Record<string, number> = {}
  for (const { value } of MIN_YIELD_OPTIONS) {
    const key = String(value)
    minCombinedYield[key] = data.filter((item) => {
      if (!matchesFilters(item, filters, ["minCombinedYield"])) return false
      if (value === null) return true
      const cy = combinedYield(item)
      return cy !== null && cy >= value
    }).length
  }

  const capTier: Record<string, number> = {}
  for (const { value } of CAP_TIER_OPTIONS) {
    capTier[value] = data.filter(
      (item) =>
        marketCapTierFromValue(item.marketCap) === value &&
        matchesFilters(item, filters, ["capTiers"])
    ).length
  }

  return {
    categories,
    recordMonths,
    deliveryMonths,
    industries,
    minBenefitValue,
    longTermBonus,
    relevance,
    minDividendYield,
    minBenefitYield,
    minCombinedYield,
    capTier,
  }
}
